import React, { useMemo, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowRight, CheckCircle2, Mail, Clock, ShieldCheck } from 'lucide-react';
import { LEGAL_ENTITY_NAME, LEGAL_REGISTERED_ADDRESS, LEGAL_SUPPORT_EMAIL } from '../legalInfo';
import './Contact.css';

export const Contact = () => {
  const reduced = useReducedMotion();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    name: '',
    email: '',
    company: '',
    agentType: 'Conversational Agent',
    message: '',
  });

  const canSubmit = useMemo(
    () => Boolean(form.name.trim() && form.email.trim() && form.message.trim()),
    [form]
  );

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError('');

    try {
      const payload = {
        name: form.name,
        email: form.email,
        company: form.company,
        message: `Agent type: ${form.agentType}\n\n${form.message}`,
      };
      const response = await fetch(
        'https://liceron-backend-1044318315452.us-central1.run.app/api/sales-request',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );
      if (!response.ok) throw new Error('Request failed');
      setSubmitted(true);
    } catch (err) {
      const subject = `Scoping request — ${form.agentType} — ${form.name}`;
      const body = `Name: ${form.name}\nEmail: ${form.email}\nCompany: ${form.company || 'Not provided'}\nAgent type: ${form.agentType}\n\nWhat they need:\n${form.message}`;
      window.location.href = `mailto:sales@liceron.in?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  const fade = {
    hidden: { opacity: 0, y: reduced ? 0 : 20 },
    visible: (i = 0) => ({
      opacity: 1,
      y: 0,
      transition: { duration: reduced ? 0.2 : 0.6, delay: reduced ? 0 : i * 0.08, ease: [0.16, 1, 0.3, 1] },
    }),
  };

  return (
    <div className="contact-page">
      <div className="contact-grid">
        {/* Left — pitch */}
        <motion.div className="contact-intro" initial="hidden" animate="visible" variants={fade}>
          <h1 className="contact-title">Tell us what you need.</h1>
          <p className="contact-lead">
            Every project starts with a free scoping call. No commitment — just clarity on what your
            agent does, what it integrates with, and how fast we can ship it.
          </p>
          <ul className="contact-points">
            <li><Clock size={17} /> We’ll scope it within 24 hours</li>
            <li><ShieldCheck size={17} /> No commitment required</li>
            <li><Mail size={17} /> Or email <a href="mailto:sales@liceron.in">sales@liceron.in</a></li>
          </ul>
        </motion.div>

        {/* Right — form */}
        <motion.div className="contact-form-wrap" initial="hidden" animate="visible" variants={fade} custom={1}>
          {!submitted ? (
            <form className="contact-form" onSubmit={onSubmit}>
              <div className="field-row">
                <label className="field">
                  <span>Name *</span>
                  <input name="name" value={form.name} onChange={onChange} placeholder="Your name" required />
                </label>
                <label className="field">
                  <span>Email *</span>
                  <input name="email" type="email" value={form.email} onChange={onChange} placeholder="you@company.com" required />
                </label>
              </div>

              <div className="field-row">
                <label className="field">
                  <span>Company</span>
                  <input name="company" value={form.company} onChange={onChange} placeholder="Company (optional)" />
                </label>
                <label className="field">
                  <span>Agent type</span>
                  <select name="agentType" value={form.agentType} onChange={onChange}>
                    <option>Conversational Agent</option>
                    <option>Workflow Agent</option>
                    <option>Data &amp; Research Agent</option>
                    <option>Enterprise AI</option>
                    <option>Not sure yet</option>
                  </select>
                </label>
              </div>

              <label className="field">
                <span>What do you want the agent to do? *</span>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={onChange}
                  rows={6}
                  placeholder="Example: A WhatsApp support agent that answers order questions, tracks shipments via our API, and escalates to a human when it can't resolve."
                  required
                />
              </label>

              <button className="contact-submit" type="submit" disabled={submitting || !canSubmit}>
                <span>{submitting ? 'Sending…' : 'Book a Scoping Call'}</span>
                <ArrowRight size={18} />
              </button>

              {error ? <div className="contact-error">{error}</div> : null}
            </form>
          ) : (
            <div className="contact-success">
              <div className="contact-success-icon">
                <CheckCircle2 size={24} />
              </div>
              <h3>Request received</h3>
              <p>Thanks — we’ll get back to you within 24 hours with a scoping plan.</p>
            </div>
          )}
        </motion.div>
      </div>

      <div className="contact-grievance">
        <h2>Company Information &amp; Grievance</h2>
        <p className="contact-grievance-name">{LEGAL_ENTITY_NAME}</p>
        <p>Registered address: {LEGAL_REGISTERED_ADDRESS}</p>
        <p>
          For grievances or complaints, write to{' '}
          <a href={`mailto:${LEGAL_SUPPORT_EMAIL}`}>{LEGAL_SUPPORT_EMAIL}</a>.
        </p>
      </div>
    </div>
  );
};
